import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, BRANCHES, WAREHOUSES } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const InterDepotTransfer = NiceModal.create(({ orders }) => {
	const dispatch = useDispatch();
	const modal = useModal();

	const schema = useMemo(
		() =>
			yup.object().shape(
				{
					warehouseID: yup
						.number()
						.nullable()
						.when('branchID', { is: (branchID) => !branchID, then: (schema) => schema.required() }),
					branchID: yup
						.number()
						.nullable()
						.when('warehouseID', {
							is: (warehouseID) => !warehouseID,
							then: (schema) => schema.required()
						}),
					comments: yup.string().nullable()
				},
				['warehouseID', 'branchID']
			),
		[]
	);

	const axiosPrivate = useAxiosPrivate();

	const [isLoading, setIsLoading] = useState(false);
	const { showSuccess, showError } = useAlert();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			warehouseID: null,
			branchID: null,
			orderNumber: `IDT-${orders[0].orderID}`,
			orderDate: new Date().toISOString(),
			comments: null,
			name: null,
			phone: null,
			email: null,
			city: null,
			address: null,
			lat: null,
			lng: null
		}
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isDirty }
	} = formMethods;

	const updateDetails = (type, id) => {
		setIsLoading(true);
		axiosPrivate
			.post(type === 'warehouse' ? WAREHOUSES.GET_WAREHOUSE : BRANCHES.GET_BRANCH, {
				ID: id
			})
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					setValue('name', data.name ?? null, { shouldValidate: true, shouldDirty: true });
					setValue('phone', data.phone ?? null, { shouldValidate: true, shouldDirty: true });
					setValue('email', data.email ?? null, { shouldValidate: true, shouldDirty: true });
					setValue('city', data.address.cityName ?? null, { shouldValidate: true, shouldDirty: true });
					setValue('address', data.address.text ?? null, { shouldValidate: true, shouldDirty: true });
					setValue('lat', data.address.lat ?? null, { shouldValidate: true, shouldDirty: true });
					setValue('lng', data.address.lng ?? null, { shouldValidate: true, shouldDirty: true });
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const onSubmit = async ({ branchID, warehouseID, comments }) => {
		setIsLoading(true);
		const postData = { branchID, warehouseID, comments, orderIDs: orders.map((a) => a.ID) };
		try {
			const response = await axiosPrivate.post(ADVANCE_WMS_MODULE.INTER_DEPOT_TRANSFER, postData);

			if (response.status === 200) {
				dispatch(updateDynamicListsInPage());
				modal.remove();
				showSuccess();
			}
		} catch (error) {
			AppLogger.error(error);
			showError(
				error?.response?.data?.code && {
					message: error.response.data.code
				}
			);
		} finally {
			setIsLoading(false);
		}
	};

	const columns = React.useMemo(
		() => [
			{
				header: 'orderID',
				accessorKey: 'orderID',
				size: 50,
				showOnMobile: true
			},
			{
				header: 'packageCount',
				accessorKey: 'packageCount',
				size: 10,
				maxSize: 50,
				showOnMobile: true
			},
			{
				header: 'customerDetails',
				accessorKey: 'customerDetails',
				size: 100
			}
		],
		[]
	);

	const defaultColSpan = {
		md: 6
	};

	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const warehouseAutoComplete = watch('warehouseID');

	const branchAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const branchAutocompleteLabels = useMemo(() => ['Name'], []);
	const branchAutoComplete = watch('branchID');

	return (
		<Modal maxWidth="medium" fullwidth className="modal-orderItems">
			<ModalTitle>
				<Typography variant="p1">interDepotTransfer</Typography>
			</ModalTitle>
			<ModalContent style={{ width: '100%' }}>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="warehouse" roleName="MultiWarehouseModule">
						<AutoComplete
							name="warehouseID"
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
							value={warehouseAutoComplete}
							setValue={(value) => {
								const warehouseValue = value?.ID ?? null;
								setValue('warehouseID', warehouseValue, { shouldValidate: true, shouldDirty: true });
								if (warehouseValue !== null) {
									setValue('branchID', null);
									updateDetails('warehouse', warehouseValue);
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="branches" roleName="BranchModule">
						<AutoComplete
							name="branchID"
							autoCompleteName={AUTOCOMPLETE.BRANCHES}
							columns={branchAutocompleteColumns}
							selectionLabelAccessor={branchAutocompleteLabels}
							value={branchAutoComplete}
							setValue={(value) => {
								const branchValue = value?.ID ?? null;
								setValue('branchID', branchValue, { shouldValidate: true, shouldDirty: true });
								if (branchValue !== null) {
									setValue('warehouseID', null);
									updateDetails('branch', branchValue);
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="orderNumber" isLoading={isLoading}>
						<StringInput name="orderNumber" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="orderDate" isLoading={isLoading}>
						<DateInput name="orderDate" value={watch('orderDate')} disabled />
					</FormItem>
					<FormItem label="comments" isLoading={isLoading}>
						<TextArea name="comments" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="name" isLoading={isLoading}>
						<StringInput name="name" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="phone" isLoading={isLoading}>
						<StringInput name="phone" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading}>
						<StringInput name="email" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="city" isLoading={isLoading}>
						<StringInput name="city" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="address" isLoading={isLoading}>
						<StringInput name="address" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="latitude" isLoading={isLoading}>
						<IntegerInput name="lat" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} isLoading={isLoading}></FormItem>
					<FormItem colSpan={defaultColSpan} label="longitude" isLoading={isLoading}>
						<IntegerInput name="lng" disabled />
					</FormItem>
					<Table
						columns={columns}
						data={orders.map((order) => ({
							orderID: order.orderID,
							packageCount: order.packageCount,
							customerDetails: `Customer name: ${order.name} City: ${order.cityName}`
						}))}
						loading={isLoading}
					/>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={() => modal.remove()} />
				<Button
					loading={isLoading}
					label={'save'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (error) => AppLogger.error(error))}
				/>
			</ModalActions>
		</Modal>
	);
});

export default InterDepotTransfer;
